import { useCallback, useEffect, useRef } from "react";
import { Box, Button, Flex, Image, Text } from "@chakra-ui/react";
import { useSetRecoilState } from "recoil";
import SVG from "react-inlinesvg";
import { useTranslation } from "react-i18next";
import usePwaInstallBanner, {
  PwaBannerVariant,
} from "@/shared/hooks/usePwaInstallBanner";
import { pwaBannerHeightState } from "@/shared/store/pwaBannerStore";

/**
 * iOS 공유 시트 버튼 모양. 안내 문구 안에 인라인으로 넣는다.
 * public/icons-v1_1 에 대응하는 아이콘이 없어서 여기 직접 그린다.
 */
function IosShareGlyph() {
  return (
    <svg
      viewBox={"0 0 24 24"}
      width={14}
      height={14}
      fill={"none"}
      stroke={"currentColor"}
      strokeWidth={1.8}
      strokeLinecap={"round"}
      strokeLinejoin={"round"}
      aria-hidden={true}
      focusable={false}
      style={{ display: "inline-block", verticalAlign: "-2px" }}
    >
      <path d={"M12 15V3"} />
      <path d={"M8 7l4-4 4 4"} />
      <path d={"M5 13v7a1 1 0 001 1h12a1 1 0 001-1v-7"} />
    </svg>
  );
}

/** 안드로이드 크롬 우측 상단의 ⋮ 메뉴 버튼 모양. */
function AndroidMenuGlyph() {
  return (
    <svg
      viewBox={"0 0 24 24"}
      width={14}
      height={14}
      fill={"currentColor"}
      aria-hidden={true}
      focusable={false}
      style={{ display: "inline-block", verticalAlign: "-2px" }}
    >
      <circle cx={12} cy={5} r={1.8} />
      <circle cx={12} cy={12} r={1.8} />
      <circle cx={12} cy={19} r={1.8} />
    </svg>
  );
}

interface PwaInstallBannerBodyProps {
  variant: PwaBannerVariant;
  onDismiss: () => void;
  onDismissForever: () => void;
}

/**
 * variant가 확정된 뒤에만 마운트된다. 배너 높이를 밖으로 알리는 effect가
 * 여기 있어야 언마운트 시 0으로 되돌리는 처리가 자연스럽게 붙는다.
 */
function PwaInstallBannerBody({
  variant,
  onDismiss,
  onDismissForever,
}: PwaInstallBannerBodyProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const setBannerHeight = useSetRecoilState(pwaBannerHeightState);

  // 레이아웃은 CSS 변수로 비키고(스크롤 컨테이너마다 패딩을 붙이지 않아도 된다),
  // 채널톡 런처는 px 좌표를 계산하므로 atom으로 같은 값을 넘긴다.
  const publishHeight = useCallback(
    (height: number) => {
      document.documentElement.style.setProperty(
        "--pwa-banner-height",
        `${height}px`
      );
      setBannerHeight(height);
    },
    [setBannerHeight]
  );

  useEffect(() => {
    const element = rootRef.current;
    if (!element) {
      return;
    }

    const measure = () => publishHeight(element.getBoundingClientRect().height);
    measure();

    // 문구 길이(ko/en/it)와 iOS 텍스트 크기 설정에 따라 2줄↔3줄로 바뀌므로 실측한다.
    // ResizeObserver는 Safari 13.1+ 라서 그 이하에서는 resize 이벤트로만 따라간다.
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => {
        window.removeEventListener("resize", measure);
        publishHeight(0);
      };
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
      publishHeight(0);
    };
  }, [publishHeight]);

  return (
    <Box
      ref={rootRef}
      role={"region"}
      aria-label={t("pwa_install.aria.region")}
      position={"fixed"}
      bottom={0}
      left={0}
      right={0}
      // 생짜 숫자를 쓰면 안 된다. 모바일 GNB가 Chakra Drawer(modal, 1400)이므로
      // 그보다 커지면 열린 메뉴 하단을 배너가 덮는다. 토스트(1700)보다도 낮아야 한다.
      zIndex={"banner"}
      background={"#FFFFFF"}
      borderTop={"1px solid #EFEFED"}
      boxShadow={"0 -4px 12px rgba(0, 0, 0, 0.08)"}
      padding={"12px 12px 12px 16px"}
    >
      <Flex gap={"12px"} alignItems={"flex-start"}>
        <Image
          src={"/apple-touch-icon.png"}
          alt={""}
          width={"40px"}
          height={"40px"}
          borderRadius={"8px"}
          flexShrink={0}
        />
        <Box flex={"1"} minWidth={0}>
          <Text fontSize={"14px"} fontWeight={"700"} color={"#242424"}>
            {t("pwa_install.title")}
          </Text>
          <Text
            marginTop={"2px"}
            fontSize={"12px"}
            lineHeight={"18px"}
            color={"#8F8F8C"}
          >
            {variant === "ios-safari-guide" && (
              <>
                <IosShareGlyph /> {t("pwa_install.ios.safari_guide")}
              </>
            )}
            {variant === "ios-open-safari" && t("pwa_install.ios.open_safari")}
            {variant === "android-chrome-guide" && (
              <>
                <AndroidMenuGlyph /> {t("pwa_install.android.chrome_guide")}
              </>
            )}
            {variant === "android-open-chrome" &&
              t("pwa_install.android.open_chrome")}
          </Text>
          <Button
            variant={"link"}
            onClick={onDismissForever}
            marginTop={"6px"}
            padding={"4px 0"}
            fontSize={"12px"}
            fontWeight={"400"}
            color={"#8F8F8C"}
            textDecoration={"underline"}
          >
            {t("pwa_install.dismiss_forever")}
          </Button>
        </Box>
        <Button
          variant={"ghost"}
          onClick={onDismiss}
          aria-label={t("pwa_install.aria.close")}
          minWidth={"44px"}
          height={"44px"}
          padding={0}
          flexShrink={0}
        >
          <SVG src={"/icons-v1_1/x.svg"} />
        </Button>
      </Flex>
    </Box>
  );
}

export default function PwaInstallBanner() {
  const { variant, dismiss, dismissForever } = usePwaInstallBanner();

  if (!variant) {
    return null;
  }

  return (
    <PwaInstallBannerBody
      variant={variant}
      onDismiss={dismiss}
      onDismissForever={dismissForever}
    />
  );
}
